import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import { GET_JOB_FUNNEL_LIST, GET_JOB_FUNNEL_LIST_FACETS } from '@bloomays-lib/adapter.api-talent';
import { JobType } from '@bloomays-lib/types.shared';
import { convertDateToStringFormat, getJobTypeGroupLabel } from '@bloomays-lib/utils.shared';
import {
  DataGridV2,
  TONES,
  avatarToneFromSeed,
  type DataGridV2Column,
  type DataGridV2QuickFilter,
} from '@bloomays-lib/ui.shared/datagrid';

import { PageHeader } from '../molecules/PageHeader';
import { errorLogger } from '../../helpers/error';

/* ── server-side filter vocabulary (mirrors the GraphQL enums) ─────────────── */

type JobFunnelListTab = 'WIN' | 'LOST' | 'IN_PROGRESS';
type JobFunnelListJobTypeGroup = 'FREELANCE' | 'CDI';
type JobFunnelListPeriod = 'LAST_3_MONTHS' | 'LAST_6_MONTHS' | 'YEAR_2025' | 'ALL';
type JobFunnelListSort = 'RECENT' | 'OLDEST' | 'PLACEMENTS_FIRST';

const DEFAULT_TAB: JobFunnelListTab = 'WIN';
const DEFAULT_SORT: JobFunnelListSort = 'RECENT';
const PAGE_SIZE = 100;

type Recruiter = { email: string; firstname: string; lastname: string };

type JobFunnelListItem = {
  id: string;
  name: string;
  jobTitle: string;
  society: { name: string };
  jobType: JobType;
  dealType?: string | null;
  status?: 'WIN' | 'LOST' | null;
  winDate?: string | null;
  createdAt?: string | null;
  expectedStartDate?: string | null;
  recruiter?: Recruiter | null;
  placedTalentName?: string | null;
  funnel: FunnelStage[];
};

type FunnelStage = { key: string; count: number };

const stageCount = (funnel: FunnelStage[], key: string): number =>
  funnel.find((stage) => stage.key === key)?.count ?? 0;

type GetJobFunnelListData = {
  getJobFunnelList: {
    totalCount: number;
    tabCounts: { win: number; lost: number; inProgress: number };
    items: JobFunnelListItem[];
  };
};

type GetJobFunnelListFacetsData = {
  getJobFunnelListFacets: {
    clients: string[];
    recruiters: Recruiter[];
  };
};

/* Synthetic quick-filter fields kept in a single controlled object. */
type FilterValues = {
  society: string;
  recruiterEmail: string;
  jobTypeGroup: string;
  period: string;
};

const EMPTY_FILTERS: FilterValues = { society: '', recruiterEmail: '', jobTypeGroup: '', period: '' };

const formatDate = (value?: string | null): string => convertDateToStringFormat(value, 'dd/MM/yyyy');

const recruiterInitials = (recruiter?: Recruiter | null): string => {
  if (!recruiter) return '—';
  const initials = `${recruiter.firstname?.[0] ?? ''}${recruiter.lastname?.[0] ?? ''}`.toUpperCase();
  return initials || (recruiter.email?.[0]?.toUpperCase() ?? '—');
};

// Prénom + nom (comme les autres écrans v2 ; la créa, elle, n'affiche que le prénom).
const recruiterFullName = (recruiter: Recruiter): string =>
  `${recruiter.firstname ?? ''} ${recruiter.lastname ?? ''}`.trim() || recruiter.email;

const societyInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '—';

const JobFunnelProjectDashboardV2 = () => {
  const { t } = useTranslation(['jobs']);
  const navigate = useNavigate();

  const [tab, setTab] = useState<JobFunnelListTab>(DEFAULT_TAB);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
  const [sort, setSort] = useState<JobFunnelListSort>(DEFAULT_SORT);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  // Bumped on "Réinitialiser" to remount the grid and clear its internal search box.
  const [resetNonce, setResetNonce] = useState(0);

  const input = useMemo(
    () => ({
      page,
      pageSize,
      search: search.trim() || undefined,
      tab,
      society: filters.society || undefined,
      recruiterEmail: filters.recruiterEmail || undefined,
      jobTypeGroup: (filters.jobTypeGroup || undefined) as JobFunnelListJobTypeGroup | undefined,
      period: (filters.period || undefined) as JobFunnelListPeriod | undefined,
      sort,
    }),
    [page, pageSize, search, tab, filters, sort],
  );

  const { data, loading, error, refetch } = useQuery<GetJobFunnelListData>(GET_JOB_FUNNEL_LIST, {
    variables: { input },
    context: { clientName: 'api.talents' },
    fetchPolicy: 'cache-and-network',
    onError: (err) => errorLogger(err, { extraInfos: 'TEC-3088 getJobFunnelList' }),
  });

  const { data: facetsData } = useQuery<GetJobFunnelListFacetsData>(GET_JOB_FUNNEL_LIST_FACETS, {
    context: { clientName: 'api.talents' },
    onError: (err) => errorLogger(err, { extraInfos: 'TEC-3088 getJobFunnelListFacets' }),
  });

  const rows = data?.getJobFunnelList.items ?? [];
  const totalCount = data?.getJobFunnelList.totalCount ?? 0;
  const tabCounts = data?.getJobFunnelList.tabCounts ?? { win: 0, lost: 0, inProgress: 0 };

  const clientOptions = useMemo(
    () => (facetsData?.getJobFunnelListFacets.clients ?? []).map((client) => ({ value: client, label: client })),
    [facetsData],
  );
  const recruiterOptions = useMemo(
    () =>
      (facetsData?.getJobFunnelListFacets.recruiters ?? []).map((recruiter) => ({
        value: recruiter.email,
        label: `${recruiter.firstname} ${recruiter.lastname}`.trim() || recruiter.email,
      })),
    [facetsData],
  );

  const resetPage = useCallback(() => setPage(0), []);

  const handleTabChange = useCallback(
    (key: string) => {
      setTab(key as JobFunnelListTab);
      resetPage();
    },
    [resetPage],
  );

  const handleServerSearch = useCallback(
    (value: string) => {
      setSearch(value);
      resetPage();
    },
    [resetPage],
  );

  const handleFilterValuesChange = useCallback(
    (values: Record<string, string>) => {
      setFilters({
        society: values.society ?? '',
        recruiterEmail: values.recruiterEmail ?? '',
        jobTypeGroup: values.jobTypeGroup ?? '',
        period: values.period ?? '',
      });
      resetPage();
    },
    [resetPage],
  );

  const handleSortChange = (value: JobFunnelListSort) => {
    setSort(value);
    resetPage();
  };

  const hasActiveFilters =
    tab !== DEFAULT_TAB ||
    sort !== DEFAULT_SORT ||
    search.trim() !== '' ||
    filters.society !== '' ||
    filters.recruiterEmail !== '' ||
    filters.jobTypeGroup !== '' ||
    filters.period !== '';

  const handleReset = () => {
    setTab(DEFAULT_TAB);
    setSearch('');
    setFilters(EMPTY_FILTERS);
    setSort(DEFAULT_SORT);
    setPage(0);
    setResetNonce((nonce) => nonce + 1);
  };

  const quickFilters = useMemo<DataGridV2QuickFilter<JobFunnelListItem>[]>(
    () => [
      { field: 'society', label: t('list.filterAllClients'), options: clientOptions, predicate: () => true },
      {
        field: 'recruiterEmail',
        label: t('list.filterAllRecruiters'),
        options: recruiterOptions,
        predicate: () => true,
      },
      {
        field: 'jobTypeGroup',
        label: t('list.filterAllTypes'),
        options: [
          { value: 'FREELANCE', label: t('list.typeFreelance') },
          { value: 'CDI', label: t('list.typeCdi') },
        ],
        predicate: () => true,
      },
      {
        field: 'period',
        label: t('list.periodAll'),
        options: [
          { value: 'LAST_3_MONTHS', label: t('list.period3Months') },
          { value: 'LAST_6_MONTHS', label: t('list.period6Months') },
          { value: 'YEAR_2025', label: t('list.period2025') },
        ],
        predicate: () => true,
      },
    ],
    [clientOptions, recruiterOptions, t],
  );

  const columns = useMemo<DataGridV2Column<JobFunnelListItem>[]>(
    () => [
      {
        field: 'name',
        header: t('list.colJobClient'),
        minWidth: 280,
        flex: 1.4,
        sortable: false,
        filterable: false,
        groupable: false,
        render: (row) => {
          const tone = avatarToneFromSeed(row.society.name || row.name);
          const closedDate = row.winDate ? formatDate(row.winDate) : null;
          const meta = [
            row.society.name,
            getJobTypeGroupLabel(row.jobType),
            closedDate
              ? t('list.metaClosed', { date: closedDate })
              : row.createdAt
              ? t('list.metaCreated', { date: formatDate(row.createdAt) })
              : null,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <JobCell>
              <SocietyBadge style={{ background: TONES[tone].color }}>{societyInitials(row.society.name)}</SocietyBadge>
              <JobText>
                <JobTitle>{row.name}</JobTitle>
                <JobMeta>{meta}</JobMeta>
              </JobText>
            </JobCell>
          );
        },
      },
      {
        field: 'pipeline',
        header: t('list.colPipeline'),
        minWidth: 470,
        flex: 1.6,
        sortable: false,
        filterable: false,
        groupable: false,
        render: (row) => {
          // Résumé aligné sur l'entonnoir (source unique) : « présentés » = talents
          // ayant atteint la phase Présenté au client, « placé » = devenus Bloomer.
          const presented = stageCount(row.funnel, 'presented');
          const placed = stageCount(row.funnel, 'placed');
          return (
            <Tooltip title={<FunnelTooltip stages={row.funnel} t={t} />} arrow>
              <PipelineCell>
                <MiniFunnel stages={row.funnel} />
                <PipelineMeta>
                  <strong>{presented}</strong> {t('list.presented')}
                  {' · '}
                  <PipelinePlaced>{placed}</PipelinePlaced> {t('list.placed')}
                </PipelineMeta>
              </PipelineCell>
            </Tooltip>
          );
        },
      },
      {
        field: 'status',
        header: t('list.colStatus'),
        minWidth: 130,
        variant: 'chip',
        sortable: false,
        filterable: false,
        groupable: false,
        chip: (row) => {
          if (row.status === 'WIN') return { label: t('list.statusWin'), tone: 'success', dot: true };
          if (row.status === 'LOST') return { label: t('list.statusLost'), tone: 'danger', dot: true };
          return { label: t('list.statusInProgress'), tone: 'info', dot: true };
        },
      },
      {
        field: 'recruiter',
        header: t('list.colRecruiter'),
        minWidth: 200,
        variant: 'avatar',
        sortable: false,
        filterable: false,
        groupable: false,
        avatar: (row) =>
          row.recruiter
            ? { initials: recruiterInitials(row.recruiter), name: recruiterFullName(row.recruiter) }
            : { initials: '—', name: '—' },
      },
      {
        field: 'action',
        header: '',
        width: 120,
        align: 'right',
        sortable: false,
        filterable: false,
        groupable: false,
        hideable: false,
        render: (row) => (
          <ActionLink>
            {row.status ? t('list.actionReview') : t('list.actionFollow')}
            <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
          </ActionLink>
        ),
      },
    ],
    [t],
  );

  const sortOptions: { value: JobFunnelListSort; label: string }[] = [
    { value: 'RECENT', label: t('list.sortRecent') },
    { value: 'OLDEST', label: t('list.sortOldest') },
    { value: 'PLACEMENTS_FIRST', label: t('list.sortPlacements') },
  ];

  return (
    <Container>
      <PageHeader
        title={t('list.title')}
        subtitle={t('list.subtitle')}
        onRefresh={async () => {
          await refetch();
        }}
      />

      {error && !data ? (
        <ErrorText>{t('list.loadingError')}</ErrorText>
      ) : (
        <DataGridV2<JobFunnelListItem>
          key={`job-funnel-list-${resetNonce}`}
          uniqueId="job-funnel-list-v2"
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          loadingVariant="bar"
          // tabs (server-driven; counts from the server)
          tabs={[
            { key: 'WIN', label: t('list.tabWin'), count: tabCounts.win },
            { key: 'LOST', label: t('list.tabLost'), count: tabCounts.lost },
            { key: 'IN_PROGRESS', label: t('list.tabInProgress'), count: tabCounts.inProgress },
          ]}
          activeTabKey={tab}
          onTabChange={handleTabChange}
          // search + filters (server-driven)
          search
          searchPlaceholder={t('list.searchPlaceholder')}
          filterMode="server"
          onServerSearch={handleServerSearch}
          quickFilters={quickFilters}
          quickFilterValues={filters}
          onQuickFilterValuesChange={handleFilterValuesChange}
          toolbarActions={
            <ToolbarRight>
              <SortSelect
                aria-label={t('list.sortLabel')}
                value={sort}
                onChange={(event) => handleSortChange(event.target.value as JobFunnelListSort)}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SortSelect>
              {hasActiveFilters ? (
                <ResetLink type="button" onClick={handleReset}>
                  {t('list.reset')}
                </ResetLink>
              ) : null}
            </ToolbarRight>
          }
          enableExport={false}
          enableFilterPanel={false}
          enableColumnSelector={false}
          rowGrouping={false}
          // server pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSize={PAGE_SIZE}
          noRowsLabel={t('list.empty')}
          onRowClick={(row) => navigate(`/jobs/${row.id}`)}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(255, 51, 40, 0.06)' },
          }}
        />
      )}
    </Container>
  );
};

export default JobFunnelProjectDashboardV2;

/* ── styles ─────────────────────────────────────────────────────────────── */

const Container = styled('div')(() => ({
  margin: 'auto',
  width: '90%',
}));

const ErrorText = styled('p')(({ theme }) => ({
  color: theme.palette.error?.main ?? '#991b1b',
  marginTop: 24,
}));

const JobCell = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minWidth: 0,
}));

const SocietyBadge = styled('div')(() => ({
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: '#fff',
}));

const JobText = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
}));

const JobTitle = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.neutral?.[90] ?? '#212121',
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const JobMeta = styled('span')(() => ({
  fontSize: 12,
  color: '#9898A8',
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const PipelineCell = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 9,
  minWidth: 0,
  paddingTop: 4,
  paddingBottom: 4,
}));

/**
 * Mini-funnel en escalier : une barre par étape réelle du pipeline (qualifié →
 * présenté → en entretien → placé), dérivée des phases en base (cf. talent
 * board). Les hauteurs sont proportionnelles au nombre de talents atteignant
 * chaque étape (atteinte cumulée), donc toujours décroissantes.
 */
const FUNNEL_MIN_H = 16;
const FUNNEL_MAX_H = 40;

/**
 * Couleurs du funnel puisées dans la palette du thème (mêmes teintes que la
 * créa) : bleu → vert lime → ambre → vert foncé.
 */
const useFunnelColors = (): Record<string, string> => {
  const theme = useTheme();
  return {
    qualified: theme.palette.blue[60],
    presented: theme.palette.green[60],
    interview: theme.palette.other.yellowMedium,
    placed: theme.palette.green[100],
  };
};

const MiniFunnel = ({ stages }: { stages: FunnelStage[] }) => {
  const colors = useFunnelColors();
  const max = Math.max(0, ...stages.map((stage) => stage.count));
  return (
    <FunnelRow>
      {stages.map((stage) => (
        <FunnelSegment
          key={stage.key}
          style={{
            background: colors[stage.key] ?? colors.qualified,
            height: max > 0 ? FUNNEL_MIN_H + (FUNNEL_MAX_H - FUNNEL_MIN_H) * (stage.count / max) : FUNNEL_MIN_H,
            opacity: max > 0 ? 1 : 0.3,
            boxShadow: stage.key === 'placed' && stage.count > 0 ? `0 0 0 2px ${colors.placed}40` : 'none',
          }}
        />
      ))}
    </FunnelRow>
  );
};

const FunnelTooltip = ({ stages, t }: { stages: FunnelStage[]; t: (key: string) => string }) => {
  const colors = useFunnelColors();
  return (
    <FunnelTooltipList>
      {stages.map((stage) => (
        <li key={stage.key}>
          <FunnelTooltipDot style={{ background: colors[stage.key] ?? colors.qualified }} />
          {t(`list.funnelStage.${stage.key}`)} : <strong>{stage.count}</strong>
        </li>
      ))}
    </FunnelTooltipList>
  );
};

const FunnelRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: 10,
  height: FUNNEL_MAX_H,
}));

// Barres nettement plus larges que hautes (rectangles horizontaux arrondis, façon créa).
const FunnelSegment = styled('span')(() => ({
  width: 58,
  borderRadius: 8,
  flexShrink: 0,
}));

const FunnelTooltipList = styled('ul')(() => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  '& li': { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 },
  '& strong': { marginLeft: 2 },
}));

const FunnelTooltipDot = styled('span')(() => ({
  width: 8,
  height: 8,
  borderRadius: 2,
  flexShrink: 0,
}));

const PipelineMeta = styled('span')(() => ({
  fontSize: 12.5,
  color: '#4A4A5A',
  '& strong': { fontWeight: 700, color: '#212121' },
}));

const PipelinePlaced = styled('strong')(() => ({
  color: '#4D7236',
}));

const ActionLink = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  fontSize: 13,
  fontWeight: 600,
  color: '#6B6B7E',
  whiteSpace: 'nowrap',
}));

const ToolbarRight = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 12,
}));

const SortSelect = styled('select')(() => ({
  padding: '7px 28px 7px 11px',
  border: '1px solid #E4E4EA',
  borderRadius: 8,
  fontFamily: 'inherit',
  fontSize: 12.5,
  color: '#4A4A5A',
  background: '#fff',
  cursor: 'pointer',
  outline: 'none',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239898A8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 9px center',
}));

const ResetLink = styled('button')(() => ({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
  color: '#FF3328',
  textDecoration: 'underline',
  padding: '4px 2px',
  '&:hover': { color: '#d92c22' },
}));
